// Porta JS de `src/project/disbursement/allocate.js` do back-end (commit 59da111).
// Precisa bater centavo a centavo com o motor do BE: mesmo split exato de
// maior-resto em BigInt, mesma validação, mesmo desempate (resto exato desc,
// depois índice asc).

const assertCents = (value) => {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError("totalCents must be a non-negative safe integer");
  }
  return value;
};

// Exact largest-remainder split using BigInt so it is correct for every safe-integer
// input, even where `totalCents * weight` would exceed Number.MAX_SAFE_INTEGER (2^53)
// and float division/floor could silently overshoot the true integer floor by 1.
// `weights` must already be non-negative safe integers (validated by both public
// callers, `allocateCents` and `allocateProportional`, before reaching here).
const largestRemainder = (totalCents, weights) => {
  const total = BigInt(totalCents);
  const bigWeights = weights.map((weight) => BigInt(weight));
  // `BigInt(0)`, não o literal `0n`: o literal é SINTAXE, então um navegador sem
  // suporte a BigInt falha ao PARSEAR o bundle inteiro (nada da aplicação carrega),
  // enquanto a chamada só falha se este caminho for executado. Mesmo piso de
  // navegador do BE (Chrome 67+/Safari 14+), declarado no PR — sem browserslist novo.
  const weightSum = bigWeights.reduce((sum, weight) => sum + weight, BigInt(0));

  const floors = new Array(weights.length);
  const remainders = new Array(weights.length);
  for (let index = 0; index < weights.length; index += 1) {
    const product = total * bigWeights[index];
    floors[index] = Number(product / weightSum);
    remainders[index] = product % weightSum;
  }

  const allocated = floors.reduce((sum, part) => sum + part, 0);
  let remainder = totalCents - allocated; // exact integer, >= 0 by construction

  const order = remainders
    .map((rem, index) => ({ index, rem }))
    .sort((left, right) => {
      if (left.rem === right.rem) return left.index - right.index;
      return left.rem > right.rem ? -1 : 1;
    });
  for (const { index } of order) {
    if (remainder <= 0) break;
    floors[index] += 1;
    remainder -= 1;
  }
  return floors;
};

/**
 * Split `totalCents` across `weights` in proportion to each weight, using the
 * largest-remainder method so the parts sum to EXACTLY `totalCents` (no cent
 * lost or gained to rounding).
 *
 * Invariants:
 * - `totalCents` must be a safe non-negative integer.
 * - `weights` must be a non-empty array of safe non-negative integers
 *   (`Number.isSafeInteger(w) && w >= 0`); non-integers, negatives, `NaN`,
 *   strings, etc. throw `RangeError`. Weights need not sum to any fixed
 *   total, only to a positive sum.
 * - Remainder cents (after flooring each proportional share) are awarded to
 *   the entries with the largest exact fractional remainder; ties are broken
 *   by ascending index.
 *
 * @param {number} totalCents
 * @param {number[]} weights
 * @returns {number[]} integers summing exactly to `totalCents`
 */
export const allocateProportional = (totalCents, weights) => {
  assertCents(totalCents);
  if (!Array.isArray(weights) || weights.length === 0) {
    throw new RangeError("weights must be a non-empty array");
  }
  if (weights.some((weight) => !Number.isSafeInteger(weight) || weight < 0)) {
    throw new RangeError("weights must be safe non-negative integers");
  }
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  if (weightSum <= 0) throw new RangeError("weights must sum to a positive number");
  return largestRemainder(totalCents, weights);
};

/**
 * Split `totalCents` across `weightsBps` (basis points, must sum to exactly
 * 10000), using the same exact largest-remainder method as
 * `allocateProportional`.
 *
 * Invariants:
 * - `totalCents` must be a safe non-negative integer.
 * - `weightsBps` must be a non-empty array of safe non-negative integers
 *   summing to exactly 10000; anything else throws `RangeError`.
 * - Remainder cents go to the largest exact fractional remainder first;
 *   ties are broken by ascending index.
 *
 * @param {number} totalCents
 * @param {number[]} weightsBps
 * @returns {number[]} integers summing exactly to `totalCents`
 */
export const allocateCents = (totalCents, weightsBps) => {
  assertCents(totalCents);
  if (!Array.isArray(weightsBps) || weightsBps.length === 0) {
    throw new RangeError("weightsBps must be a non-empty array");
  }
  if (weightsBps.some((bps) => !Number.isSafeInteger(bps) || bps < 0)) {
    throw new RangeError("weightsBps must be non-negative integers");
  }
  const sum = weightsBps.reduce((total, bps) => total + bps, 0);
  if (sum !== 10000) throw new RangeError("weightsBps must sum to 10000");
  return largestRemainder(totalCents, weightsBps);
};
